import { FC, useState, useEffect } from 'react';
import { GenericLink, useBreakpoint, FAIcon, Breakpoints, NextgenImage } from '@nextgen-web-framework/all';
import { MiniCart } from '@commerce-cart-webcomponents/ui';
import { SearchBar } from '@commerce-search-webcomponents/ui';
import { SearchOverlay } from '@commerce-search-webcomponents/ui-composite';
import { SearchCloseButton, PortalModal } from '@commerce-webcomponents/ui';
import { LocationSelector } from '../LocationSelector/LocationSelector';
import { CMSDataType } from '../../utils/common';
import styles from './MobileTopBarNavigation.module.scss';
import type { MobileTopBarNavigationPropsTypes, GetCartClassNamesProps } from './types/propsTypes';

const MobileTopBarNavigation: FC<MobileTopBarNavigationPropsTypes> = ({
  headerElement,
  topBarScrolled,
  hasCart = true,
  onSuggestionClick,
  onBackClick,
  logo,
  headerTitle,
  isStaticHeader,
  hasAmwayLogo = true,
  hasSearchBar = true,
  moreActionContent,
  isBackNavigation = true,
  hideLocationSelector = false,
  isStaticLocationSelector = false,
}) => {
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const [searchText, setSearchText] = useState<string>('');

  const viewPortType = useBreakpoint() as Breakpoints;
  const mobileView = [Breakpoints.XS, Breakpoints.SM].includes(viewPortType);
  const animationRuntime = 301;

  const onBackButtonClick = (e: React.KeyboardEvent | React.MouseEvent) => {
    if (onBackClick && typeof onBackClick === 'function') {
      onBackClick(e);
    }
  };

  // TODO: move out tranformation as well when only data is needed to render existing components
  const mobileDefaultLogo = logo?.default_logo.images?.find(
    (image: CMSDataType) => image.viewport.includes('xs') || image.viewport.includes('sm'),
  );
  const mobileTransparentLogo = logo?.transparent_logo?.images.find(
    (image: CMSDataType) => image.viewport.includes('xs') || image.viewport.includes('sm'),
  );

  useEffect(() => {
    if (mobileView) {
      if (!showSearchOverlay) {
        setTimeout(() => {
          setShowSearchOverlay(false);
        }, animationRuntime);
      } else {
        setShowSearchOverlay(true);
      }
    }
  }, [showSearchOverlay]);

  const overlayCloseHandlerOnMobile = () => {
    setSearchText('');
    setShowSearchOverlay(false);
  };

  const suggestionHandlerOnMobile = (
    e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | undefined,
    text: string,
  ) => {
    setShowSearchOverlay(false);
    onSuggestionClick(e, text);
  };

  const getSearchBarComponent = (expanded?: boolean) => (
    <SearchBar
      searchText={searchText}
      setSearchText={setSearchText}
      onSearch={suggestionHandlerOnMobile}
      openOverlay={() => setShowSearchOverlay(true)}
      expanded={expanded}
    />
  );
  const renderTitle = () => {
    let headerContent = null;
    if (headerTitle) {
      headerContent = (
        <div className={`${topBarScrolled ? styles['title-on-scroll'] : styles['title']}`}>{headerTitle}</div>
      );
    } else if (hasAmwayLogo) {
      headerContent = (
        <GenericLink forceNative={false} href={'/'}>
          <span>
            {topBarScrolled ? (
              <div className={styles['amway-logo']}>
                <NextgenImage
                  src={mobileDefaultLogo?.image.url}
                  alt={mobileDefaultLogo?.alt_text}
                  className={styles['logo']}
                  data-testid="mobileDefaultLogo-element-test-id"
                />
              </div>
            ) : (
              <div className={styles['amway-logo']}>
                <NextgenImage src={mobileTransparentLogo?.image?.url} alt={mobileTransparentLogo?.url} />
              </div>
            )}
          </span>
        </GenericLink>
      );
    }
    return headerContent;
  };

  const renderMobileHeader = () => {
    const renderBackIcon = isBackNavigation && (
      <div className={styles['mobile-top-header-icon']}>
        <FAIcon data-testid="click-icon-testid" iconName="chevron-left" onClick={(e) => onBackButtonClick(e)} />
      </div>
    );

    return (
      <>
        {headerElement ? (
          <span className={styles['mobile-top-header-title-container']}>
            {renderBackIcon}
            {(topBarScrolled || (isStaticHeader && headerElement)) && (
              <div className={styles['header-element-container']} data-testid="static-header-element-test-id">
                {headerElement}
              </div>
            )}
          </span>
        ) : (
          renderTitle()
        )}
      </>
    );
  };
  const renderTopBarLocationSelector = () =>
    (isStaticLocationSelector || topBarScrolled) && !hideLocationSelector ? (
      <span className={styles[`mobile-top-header-icon-component`]}>
        <LocationSelector showTopBarLocation={true} showMobileVersion={true} />
      </span>
    ) : (
      ''
    );
  const renderLocationSelector = () =>
    !isStaticLocationSelector && !hideLocationSelector ? <LocationSelector showMobileVersion={true} /> : null;
  const getCartClassNames = ({ isStaticHeader, hasCart, styles }: GetCartClassNamesProps) =>
    `${isStaticHeader ? styles['transparent-border'] : ''} 
              ${styles['mobile-top-header-cart-component']} 
              ${!hasCart ? styles['hidden-cart-icon'] : ''}`.trim();
  return (
    <>
      {['xs', 'sm'].includes(viewPortType) ? (
        <>
          {renderLocationSelector()}

          <header
            className={`
            ${topBarScrolled ? styles['mobile-top-header-on-scroll-container'] : ''} ${
              styles['mobile-top-header-container']
            } 
            ${
              !isStaticLocationSelector && !hideLocationSelector
                ? styles['header-top-margin-with-location']
                : styles['header-top-margin-without-location']
            }
             ${isStaticHeader ? styles['static-header'] : ''}`}
            data-testid="mobile-top-header-test-id"
          >
            <div className={styles['mobile-top-header-left-container']}>{renderMobileHeader()}</div>
            <div className={styles['mobile-top-header-right-container']}>
              {hasSearchBar ? (
                <span className={styles[`mobile-top-header-icon-component`]} data-testid="search-bar-test-id">
                  {getSearchBarComponent(false)}
                </span>
              ) : (
                <></>
              )}

              {renderTopBarLocationSelector()}

              {
                <span
                  className={getCartClassNames({ isStaticHeader, hasCart, styles })}
                  data-testid="mini-cart-header-icon-test-id"
                >
                  <GenericLink href={'/cart'} forceNative={false}>
                    <div>
                      <MiniCart
                        displayCount={0}
                        icon={<FAIcon iconName={'cart-shopping'} className={styles['mini-cart-icon']} />}
                      />
                    </div>
                  </GenericLink>
                </span>
              }
              {moreActionContent && (
                <div className={styles['mobile-top-header-icon-component']} data-testid="ellipsis-icon-test-id">
                  {moreActionContent}
                </div>
              )}
            </div>
          </header>
          {showSearchOverlay && (
            <PortalModal elementId="react-portal-overlay" customStyles>
              <div
                className={showSearchOverlay && mobileView ? styles['overlay-slide-in'] : styles['overlay-slide-out']}
              >
                <div className={styles['mobile-search-overlay-header']}>
                  {getSearchBarComponent(true)}
                  {<SearchCloseButton overlayCloseHandler={overlayCloseHandlerOnMobile} />}
                </div>
                {
                  <SearchOverlay
                    onSuggestionClick={suggestionHandlerOnMobile}
                    searchText={searchText}
                    closeHandler={overlayCloseHandlerOnMobile}
                    showOverlay={showSearchOverlay}
                  />
                }
              </div>
            </PortalModal>
          )}
        </>
      ) : (
        <div className={styles['desktop-container']}></div>
      )}
    </>
  );
};
export default MobileTopBarNavigation;
